import type { CapSpecIn, ConditionSpecIn } from "../../types";

/** Compiled onto the award step — not authorable in the conditions dropdown. */
export const COOLDOWN_SINCE_LAST_AWARD_TYPE = "cooldown_since_last_award";

export const AWARD_FREQUENCY = {
  ONE_TIME: "one_time",
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  CUSTOM: "custom",
  NO_LIMIT: "no_limit",
} as const;

export type AwardFrequency =
  (typeof AWARD_FREQUENCY)[keyof typeof AWARD_FREQUENCY];

export type AwardFrequencyUnit = "hours" | "days";

export const DEFAULT_AWARD_FREQUENCY: AwardFrequency = AWARD_FREQUENCY.ONE_TIME;

export const DEFAULT_CUSTOM_FREQUENCY_AMOUNT = "1";
export const DEFAULT_CUSTOM_FREQUENCY_UNIT: AwardFrequencyUnit = "days";

export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86400;

/** Rolling windows — not calendar resets. */
export const AWARD_FREQUENCY_SECONDS = {
  daily: 86400,
  weekly: 604800,
  monthly: 2592000,
} as const;

export type AwardFrequencyFormFields = {
  award_frequency: AwardFrequency;
  award_frequency_amount?: string;
  award_frequency_unit?: AwardFrequencyUnit;
  /**
   * Exact stored cooldown when Custom display parts would not round-trip
   * (values that are not a whole hour). Cleared if the admin edits amount/unit.
   */
  award_frequency_exact_seconds?: number;
};

export type AwardFrequencyCompileInput = {
  award_frequency?: AwardFrequency;
  award_frequency_amount?: string;
  award_frequency_unit?: AwardFrequencyUnit;
  award_frequency_exact_seconds?: number;
  max_awards_per_user?: string;
  applyAffiliateCapDefault?: boolean;
  /**
   * Count of AWARD steps. One-time and the affiliate empty-cap default use
   * this instead of a hard-coded 1 so a multi-award run does not 422.
   */
  awardStepCount?: number;
};

export type AwardFrequencyCompileResult = {
  caps?: CapSpecIn;
  cooldownSeconds: number | null;
};

const parsePositiveInt = (value: unknown): number | undefined => {
  if (value == null || value === "") return undefined;
  const parsed = typeof value === "number" ? value : parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.trunc(parsed);
};

/** Custom amount + unit → integer seconds. Undefined when the amount is blank/invalid. */
export const compileCustomSeconds = (
  amount?: string,
  unit?: AwardFrequencyUnit,
): number | undefined => {
  const parsed = parsePositiveInt(amount);
  if (parsed === undefined) return undefined;
  const multiplier = unit === "hours" ? SECONDS_PER_HOUR : SECONDS_PER_DAY;
  return parsed * multiplier;
};

/**
 * Reverse a non-preset cooldown into Custom amount + unit for the dropdown.
 * Values that are not a whole hour are shown as the nearest hour (min 1) —
 * {@link compileAwardFrequency} keeps the original seconds via
 * `award_frequency_exact_seconds` until the admin edits amount or unit.
 */
export const customPartsFromSeconds = (
  seconds: number,
): { amount: string; unit: AwardFrequencyUnit } => {
  if (seconds % SECONDS_PER_DAY === 0) {
    return { amount: String(seconds / SECONDS_PER_DAY), unit: "days" };
  }
  if (seconds % SECONDS_PER_HOUR === 0) {
    return { amount: String(seconds / SECONDS_PER_HOUR), unit: "hours" };
  }
  return {
    amount: String(Math.max(1, Math.round(seconds / SECONDS_PER_HOUR))),
    unit: "hours",
  };
};

const customDisplayMatchesSeconds = (
  seconds: number,
  amount?: string,
  unit?: AwardFrequencyUnit,
): boolean => {
  const parts = customPartsFromSeconds(seconds);
  return amount === parts.amount && unit === parts.unit;
};

export const cooldownSecondsFromSteps = (
  steps: Array<{ conditions?: ConditionSpecIn[] }>,
): number | undefined => {
  for (const step of steps) {
    const found = (step.conditions ?? []).find(
      (condition) => condition.type === COOLDOWN_SINCE_LAST_AWARD_TYPE,
    );
    if (!found) continue;
    const seconds = parsePositiveInt(found.params?.seconds);
    if (seconds !== undefined) return seconds;
  }
  return undefined;
};

type StoredStepShape = {
  conditions?: ConditionSpecIn[];
  results?: Array<{ result_type?: string }>;
};

/**
 * One-time means one run per user, so its cap is the AWARD-step count
 * (minimum 1) — the same value `compileAwardFrequency` writes.
 */
const oneTimeCapFor = (steps: StoredStepShape[]): number =>
  Math.max(
    1,
    steps.filter((step) => step.results?.[0]?.result_type === "award").length,
  );

/**
 * Reverse-map stored caps + cooldown specs onto the Award Frequency dropdown.
 * Cap equal to the AWARD-step count with no cooldown → One-time. Preset second
 * values → Daily/Weekly/Monthly. Other cooldowns → Custom. Any other cap (or
 * unset) with no cooldown → No frequency limit.
 */
export const inferAwardFrequencyFields = (
  caps: CapSpecIn | undefined,
  steps: StoredStepShape[],
): AwardFrequencyFormFields => {
  const seconds = cooldownSecondsFromSteps(steps);
  if (seconds === AWARD_FREQUENCY_SECONDS.daily) {
    return { award_frequency: AWARD_FREQUENCY.DAILY };
  }
  if (seconds === AWARD_FREQUENCY_SECONDS.weekly) {
    return { award_frequency: AWARD_FREQUENCY.WEEKLY };
  }
  if (seconds === AWARD_FREQUENCY_SECONDS.monthly) {
    return { award_frequency: AWARD_FREQUENCY.MONTHLY };
  }
  if (seconds !== undefined) {
    const parts = customPartsFromSeconds(seconds);
    const representable = compileCustomSeconds(parts.amount, parts.unit) === seconds;
    return {
      award_frequency: AWARD_FREQUENCY.CUSTOM,
      award_frequency_amount: parts.amount,
      award_frequency_unit: parts.unit,
      ...(representable ? {} : { award_frequency_exact_seconds: seconds }),
    };
  }
  if (caps?.max_awards_per_user === oneTimeCapFor(steps)) {
    return { award_frequency: AWARD_FREQUENCY.ONE_TIME };
  }
  return { award_frequency: AWARD_FREQUENCY.NO_LIMIT };
};

/**
 * Compile the dropdown into caps + cooldown seconds.
 *
 * Unset / No frequency limit leaves the stored cap and injects no cooldown.
 * One-time forces `max_awards_per_user` to the AWARD-step count (minimum 1).
 * Seconds are always integers.
 */
export const compileAwardFrequency = (
  input: AwardFrequencyCompileInput,
): AwardFrequencyCompileResult => {
  const defaultCap = Math.max(1, input.awardStepCount ?? 1);
  const fallbackCap =
    parsePositiveInt(input.max_awards_per_user) ??
    (input.applyAffiliateCapDefault ? defaultCap : undefined);
  const capsFromFallback: CapSpecIn | undefined =
    fallbackCap !== undefined
      ? { max_awards_per_user: fallbackCap }
      : undefined;

  if (input.award_frequency === AWARD_FREQUENCY.ONE_TIME) {
    return { caps: { max_awards_per_user: defaultCap }, cooldownSeconds: null };
  }

  if (
    input.award_frequency === AWARD_FREQUENCY.DAILY ||
    input.award_frequency === AWARD_FREQUENCY.WEEKLY ||
    input.award_frequency === AWARD_FREQUENCY.MONTHLY
  ) {
    return {
      caps: capsFromFallback,
      cooldownSeconds: AWARD_FREQUENCY_SECONDS[input.award_frequency],
    };
  }

  if (input.award_frequency === AWARD_FREQUENCY.CUSTOM) {
    const exact = parsePositiveInt(input.award_frequency_exact_seconds);
    const preserveExact =
      exact !== undefined &&
      customDisplayMatchesSeconds(
        exact,
        input.award_frequency_amount,
        input.award_frequency_unit,
      );
    const seconds = preserveExact
      ? exact
      : compileCustomSeconds(
          input.award_frequency_amount,
          input.award_frequency_unit,
        );
    return {
      caps: capsFromFallback,
      cooldownSeconds: seconds ?? null,
    };
  }

  return { caps: capsFromFallback, cooldownSeconds: null };
};

export const cooldownCondition = (seconds: number): ConditionSpecIn => ({
  type: COOLDOWN_SINCE_LAST_AWARD_TYPE,
  params: { seconds },
});

/**
 * Campaign-level Award Frequency: strip authored copies, then inject the same
 * cooldown onto every award step. Extra award steps share the campaign
 * frequency; non-award steps only lose any leftover cooldown row.
 */
export const withAwardFrequencyCooldown = (
  conditions: ConditionSpecIn[],
  isAwardStep: boolean,
  cooldownSeconds: number | null,
): ConditionSpecIn[] => {
  const without = conditions.filter(
    (condition) => condition.type !== COOLDOWN_SINCE_LAST_AWARD_TYPE,
  );
  if (!isAwardStep || cooldownSeconds == null) return without;
  return [...without, cooldownCondition(cooldownSeconds)];
};

export const isCooldownCondition = (condition: { type: string }): boolean =>
  condition.type === COOLDOWN_SINCE_LAST_AWARD_TYPE;

const DISPLAY_BANNER_TYPE_KEY = "display_banner";

/**
 * Hide Award Frequency only when the campaign cannot award anything:
 * at least one Display Info Banner step, and no step with a different
 * result. Blank new campaigns (empty `result_type_key`) stay visible.
 * Mixed banner + award still shows the cap control.
 */
export const isInformationalBannerOnly = (
  steps?: Array<{ result_type_key?: string } | undefined>,
): boolean => {
  const list = steps ?? [];
  const hasDisplayBanner = list.some(
    (step) => step?.result_type_key === DISPLAY_BANNER_TYPE_KEY,
  );
  if (!hasDisplayBanner) return false;
  return !list.some((step) => {
    const key = step?.result_type_key;
    return Boolean(key) && key !== DISPLAY_BANNER_TYPE_KEY;
  });
};

/**
 * Award Frequency is a PAM beta control. Braze/series frequency is owned
 * by Braze. Show the dropdown only on non-Braze PAM campaigns when beta
 * mode is on (`null` while the flag loads counts as off).
 */
export const shouldShowAwardFrequency = ({
  isDisplayBanner = false,
  isBraze = false,
  betaMode,
}: {
  isDisplayBanner?: boolean;
  isBraze?: boolean;
  betaMode: boolean | null;
}): boolean => !isDisplayBanner && !isBraze && betaMode === true;
